import {Phase1HistoryEditorApi} from "./api";
import CloneForm from "./clone-form";
import {Alert, Button, Checkbox, Drawer, Form, List, Typography} from "antd";
import {tailFormItemLayout} from "../../../../common/form";
import React, {useContext, useState} from "react";
import useBreakpoints from "../../../../hooks/useBreakpoints";
import {
    QueryGetScheduleHistoryArgs,
    ScheduleHistoryDetail,
} from "../../../../__generated__/graphql/types";
import {useQuery} from "react-apollo";
import {Queries} from "./graphql";
import {OrgDataContext} from "../../../../context/orgData";
import {ScheduledPaxNode} from "../../../../components/scheduling/types";


export interface CloneFormDrawerProps {
    api: Phase1HistoryEditorApi
}

function useCheckExistingSchedule(api: Phase1HistoryEditorApi){

    const orgData = useContext(OrgDataContext);

    const vars: QueryGetScheduleHistoryArgs = {
        groupName: api.cloneSchedule.form.getValue('name'),
        date: api.cloneSchedule.form.getValue('date')?.format('YYYY-MM-DD'),
        lastKnownController: api.groupData?.lastKnownController,
        tpID: orgData.transporterID
    }

    const queryResult = useQuery<
        {
            GetScheduleHistory: ScheduleHistoryDetail
        },
        QueryGetScheduleHistoryArgs
    >(
        Queries.GetScheduleHistory,
        {
            variables: vars,
            fetchPolicy: 'network-only',
            skip: !vars.groupName || !vars.date
        }
    )

    const data = queryResult.data?.GetScheduleHistory;

    return {
        loading: queryResult.loading,
        exists: Boolean(data)
    };
}

const CloneFormDrawer: React.FC<CloneFormDrawerProps> = ({ api }) => {
    const bp = useBreakpoints();
    const [acknowledged, setAcknowledged] = useState(false);

    const { loading, exists } = useCheckExistingSchedule(api);

    const deletedPersonPax = (api.getScheduledPaxNodes() as ScheduledPaxNode[]).filter(pax => !pax.personID);
    const hasDeletedPersonPax = deletedPersonPax.length > 0;

    return (
        <Drawer
            visible={api.cloneSchedule.drawer.isOpen()}
            width={bp.min('small') ? 500 : '100%'}
            closable
            onClose={api.cloneSchedule.drawer.close}
            title="Clone Schedule"
        >
            <CloneForm
                ref={api.cloneSchedule.ref}
                formData={api.cloneSchedule.form.get()}
                onFieldsChange={api.cloneSchedule.form.set}
            />
            {hasDeletedPersonPax && (
                <Alert
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="Some passengers cannot be cloned"
                    description={
                        <>
                            <p>
                                The following {deletedPersonPax.length === 1 ? 'passenger has' : 'passengers have'} a
                                deleted person record and will <strong>not</strong> be included in the cloned schedule.
                                They must be re-added manually.
                            </p>
                            <List
                                size="small"
                                dataSource={deletedPersonPax}
                                renderItem={pax => (
                                    <List.Item>
                                        <Typography.Text>{`${pax.lastName}, ${pax.firstName}`}</Typography.Text>
                                    </List.Item>
                                )}
                            />
                            <Checkbox
                                style={{ marginTop: 12 }}
                                checked={acknowledged}
                                onChange={e => setAcknowledged(e.target.checked)}
                            >
                                I understand these passengers will not be cloned
                            </Checkbox>
                        </>
                    }
                />
            )}
            <Form.Item
                {...tailFormItemLayout}
                validateStatus={exists ? "error" : undefined}
                hasFeedback={exists}
                help={exists ? "A schedule with this Name and Flight Date already exists. Choose another name or date." : undefined}
            >
                <Button
                    type="primary"
                    icon="copy"
                    loading={api.cloneSchedule.clone.isCloning}
                    onClick={api.cloneSchedule.clone.initiate}
                    disabled={exists || loading || (hasDeletedPersonPax && !acknowledged)}
                >
                    Clone and Edit
                </Button>
            </Form.Item>
        </Drawer>
    )
}

export default CloneFormDrawer;